import { css, html, LitElement, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import type { IconName } from '@/components/Icon/types'
import { safeCustomElement } from '@/utilities/custom-element'
import type { TextFieldLayout, TextFieldStyle } from './types'

// Simple ID generator
let idCounter = 0
function generateId(): string {
  return `mdc-text-field-${++idCounter}`
}

@safeCustomElement('mdc-text-field')
export class MdcTextField extends LitElement {
  static formAssociated = true

  @property({ type: String }) layout: TextFieldLayout = 'floating-label'
  @property({ type: String }) variant: TextFieldStyle = 'secondary'
  @property({ type: String }) label = ''
  @property({ type: String }) hint = ''
  @property({ type: String }) type = 'text'
  @property({ type: String }) placeholder = ''
  @property({ type: String, attribute: 'left-icon' }) leftIcon: IconName | '' = ''
  @property({ type: String, attribute: 'button' }) buttonStr = ''
  @property({ type: String, attribute: 'validation' }) validationStr = ''
  @property({ type: String }) value = ''
  @property({ type: String }) name = ''
  @property({ type: String }) id = ''
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) required = false
  @property({ type: Number }) maxlength = 0

  @state() private isActive = false
  private inputId = generateId()
  private _internals!: ElementInternals

  constructor() {
    super()
    this._internals = this.attachInternals()
  }

  get button() {
    if (!this.buttonStr) return null
    try {
      return JSON.parse(this.buttonStr)
    } catch {
      return null
    }
  }

  set button(value) {
    this.buttonStr = value ? JSON.stringify(value) : ''
  }

  get validation() {
    if (!this.validationStr) return null
    try {
      return JSON.parse(this.validationStr)
    } catch {
      return null
    }
  }

  set validation(value) {
    this.validationStr = value ? JSON.stringify(value) : ''
  }

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-text-field-wrapper {
			--_mdc-text-field-icon-size: var(--form-field-icon-size, 1.125rem);
			--_mdc-text-field__input-padding-sides: var(--form-field-padding-horizontal, 0.75rem);
			--_mdc-text-field__input-padding-vertical: var(--form-field-padding-vertical, 0.5rem);

			display: flex;
			flex-direction: column;
			gap: var(--spacing-050, 0.5rem);
		}

		.mdc-text-field__input {
			box-sizing: border-box;
			appearance: none;
			margin: 0;
			padding: 0;
			border: 0;
			background: none;
			display: flex;
			align-items: center;
			align-self: stretch;
			width: 100%;
			height: var(--form-field-height, 2.75rem) !important;
			font-size: var(--form-field-font-size, 0.875rem);
			color: var(--color-functional-gray-black);
			font-weight: 450;
			line-height: 1 !important;
			border: 1px solid transparent;
			border-radius: var(--radius-md, 0.5rem);
			padding: var(--_mdc-text-field__input-padding-vertical) var(--_mdc-text-field__input-padding-sides);
			background-color: var(--color-functional-gray-white);
		}

		/* breakpoints.md - 1 */
		@media (max-width: 767px) {
			.mdc-text-field__input {
				font-size: var(--form-field-font-size-mobile, 1rem);
			}
		}

		.mdc-text-field__input::placeholder {
			font-size: var(--form-field-font-size, 0.875rem);
			color: var(--color-functional-gray-200);
			font-weight: 450;
			line-height: 1;
		}

		/* breakpoints.md - 1 */
		@media (max-width: 767px) {
			.mdc-text-field__input::placeholder {
				font-size: var(--form-field-font-size-mobile, 1rem);
			}
		}

		.mdc-text-field__input:focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		.mdc-text-field-input-wrapper {
			position: relative;
		}

		.mdc-text-field__icon {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			position: absolute;
			top: 0;
			bottom: 0;
			left: var(--_mdc-text-field__input-padding-sides);
			color: var(--color-functional-gray-black);
			margin: auto;
		}

		.mdc-text-field__icon svg,
		.mdc-text-field__icon mdc-icon {
			width: var(--_mdc-text-field-icon-size);
			height: var(--_mdc-text-field-icon-size);
		}

		.mdc-text-field__button {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			position: absolute;
			top: 0;
			right: var(--_mdc-text-field__input-padding-sides);
			bottom: 0;
			color: var(--color-functional-gray-black);
			margin: auto;
		}

		.mdc-text-field-wrapper--with-icon .mdc-text-field__input {
			padding-left: calc(var(--_mdc-text-field__input-padding-sides) + var(--_mdc-text-field-icon-size) + var(--spacing-050, 0.5rem));
		}

		.mdc-text-field-wrapper--with-button .mdc-text-field__input {
			padding-right: calc(var(--_mdc-text-field__input-padding-sides) + var(--_mdc-text-field-icon-size) + var(--spacing-050, 0.5rem));
		}

		.mdc-text-field__hint {
			font-size: var(--text-075, 0.75rem);
			color: var(--color-functional-gray-400);
			font-weight: 450;
			line-height: 1.3;
		}

		.mdc-text-field-wrapper--layout-floating-label {
			--_mdc-text-field-label-margin-left: var(--_mdc-text-field__input-padding-sides);
		}

		.mdc-text-field-wrapper--layout-floating-label.mdc-text-field-wrapper--with-icon {
			--_mdc-text-field-label-margin-left: calc(var(--_mdc-text-field__input-padding-sides) + var(--_mdc-text-field-icon-size) + var(--spacing-050, 0.5rem));
		}

		.mdc-text-field-wrapper--layout-floating-label .mdc-text-field__input {
			padding-bottom: 0;
		}

		.mdc-text-field-wrapper--layout-floating-label .mdc-text-field__label {
			display: flex;
			align-items: center;
			position: absolute;
			top: 0;
			bottom: 0;
			font-size: var(--text-087-and-half, 0.875rem);
			color: var(--color-functional-gray-400);
			font-weight: 450;
			line-height: 1.3;
			margin-left: var(--_mdc-text-field-label-margin-left);
			transition: transform 0.2s cubic-bezier(0, 0, 0.2, 1);
			pointer-events: none;
			transform-origin: left top;
		}

		.mdc-text-field-input-wrapper--active .mdc-text-field__label,
		.mdc-text-field-input-wrapper:focus-within .mdc-text-field__label {
			transform: translateY(-6px) scale(0.85);
		}

		.mdc-text-field-wrapper--layout-stacked-label .mdc-text-field__label {
			font-size: var(--text-075, 0.75rem);
			color: var(--color-functional-gray-400);
			font-weight: 450;
			line-height: 1;
		}

		.mdc-text-field__label-row {
			display: flex;
			align-items: center;
			justify-content: flex-start;
			gap: var(--spacing-050, 0.5rem);
		}

		.mdc-text-field-wrapper--validation-error .mdc-text-field__input {
			border-color: var(--color-functional-red-500);
		}

		.mdc-text-field-wrapper--validation-error .mdc-text-field__input:focus-visible {
			box-shadow: 0 0 0 2px rgba(var(--color-functional-red-500-rgb, 239, 68, 68), 0.5);
			outline: none;
		}

		.mdc-text-field-wrapper--validation-error .mdc-text-field__hint {
			color: var(--color-functional-red-500);
		}

		.mdc-text-field__input:disabled {
			color: var(--color-functional-gray-300);
			background-color: var(--color-functional-gray-50);
		}

		.mdc-text-field__input:disabled::placeholder {
			color: var(--color-functional-gray-200);
		}

		.mdc-text-field-wrapper--disabled .mdc-text-field__icon {
			color: var(--color-functional-gray-300);
		}

		.mdc-text-field-wrapper--disabled .mdc-text-field__label {
			color: var(--color-functional-gray-400);
		}

		.mdc-text-field-wrapper--style-secondary .mdc-text-field__input {
			border-color: var(--color-functional-gray-100);
		}

		.mdc-text-field-wrapper--style-tertiary .mdc-text-field__input {
			transition: background-color 0.2s ease;
		}

		.mdc-text-field-wrapper--style-tertiary .mdc-text-field__input:hover:not(:disabled),
		.mdc-text-field-wrapper--style-tertiary .mdc-text-field__input:focus-visible:not(:disabled) {
			background-color: var(--color-functional-gray-25);
		}

		.mdc-text-field-wrapper--style-tertiary .mdc-text-field__input:disabled {
			background-color: var(--color-functional-gray-50);
		}

		.mdc-text-field__input[type='date']::-webkit-inner-spin-button,
		.mdc-text-field__input[type='date']::-webkit-calendar-picker-indicator {
			display: none;
			-webkit-appearance: none;
		}
	`

  connectedCallback() {
    super.connectedCallback()
    if (this.id) {
      this.inputId = this.id
    } else if (!this.inputId) {
      this.inputId = generateId()
    }
    this.updateIsActive()
    this._internals.setFormValue(this.value || null)
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties)
    if (
      changedProperties.has('value') ||
      changedProperties.has('placeholder') ||
      changedProperties.has('type')
    ) {
      this.updateIsActive()
    }
    if (changedProperties.has('value') || changedProperties.has('name')) {
      this._internals.setFormValue(this.value || null)
      this._updateValidity()
    }
  }

  private _updateValidity() {
    const input = this.shadowRoot?.querySelector('input')
    if (input) {
      this._internals.setValidity(input.validity, input.validationMessage, input)
    }
  }

  formResetCallback() {
    this.value = ''
    this._internals.setFormValue(null)
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled
  }

  formStateRestoreCallback(state: string) {
    this.value = state
    this._internals.setFormValue(this.value || null)
  }

  private updateIsActive() {
    this.isActive = Boolean(this.placeholder || this.value || this.type === 'date')
  }

  private handleFocus(e: FocusEvent) {
    this.isActive = true
    this.dispatchEvent(
      new CustomEvent('focus', {
        detail: { originalEvent: e },
        bubbles: true,
        composed: true,
      })
    )
  }

  private handleBlur(e: FocusEvent) {
    const input = e.target as HTMLInputElement
    if (!input.value) {
      this.isActive = false
    }
    this.dispatchEvent(
      new CustomEvent('blur', {
        detail: { originalEvent: e },
        bubbles: true,
        composed: true,
      })
    )
  }

  private handleChange(e: Event) {
    const input = e.target as HTMLInputElement
    this.value = input.value
    this._internals.setFormValue(this.value || null)
    this._updateValidity()
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { value: this.value, originalEvent: e },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    const button = this.button
    const validation = this.validation
    const classes = [
      'mdc-text-field-wrapper',
      `mdc-text-field-wrapper--layout-${this.layout}`,
      `mdc-text-field-wrapper--style-${this.variant}`,
      this.leftIcon ? 'mdc-text-field-wrapper--with-icon' : '',
      button ? 'mdc-text-field-wrapper--with-button' : '',
      validation ? `mdc-text-field-wrapper--validation-${validation.status}` : '',
      this.disabled ? 'mdc-text-field-wrapper--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ')

    const inputWrapperClasses = `mdc-text-field-input-wrapper${this.isActive ? ' mdc-text-field-input-wrapper--active' : ''}`

    return html`
			<div class=${classes}>
				${
          this.layout === 'stacked-label'
            ? html`<div class="mdc-text-field__label-row">
                <label for=${this.inputId} class="mdc-text-field__label">${this.label}</label>
                <slot name="label-action"></slot>
              </div>`
            : nothing
        }
				<div class=${inputWrapperClasses}>
					${
            this.layout === 'floating-label'
              ? html`<label for=${this.inputId} class="mdc-text-field__label">${this.label}</label>`
              : nothing
          }
					${
            this.leftIcon
              ? html`<span class="mdc-text-field__icon" aria-hidden="true"><mdc-icon name=${this.leftIcon}></mdc-icon></span>`
              : nothing
          }
					<input
						id=${this.inputId}
						class="mdc-text-field__input"
						type=${this.type}
						placeholder=${this.placeholder}
						.value=${this.value}
						name=${this.name || ''}
						maxlength=${this.maxlength > 0 ? this.maxlength : nothing}
						?disabled=${this.disabled}
						?required=${this.required}
						@focus=${this.handleFocus}
						@blur=${this.handleBlur}
						@input=${this.handleChange}
					/>
					${
            button
              ? html`
								<span class="mdc-text-field__button">
									${
                    button.type === 'iconToggle'
                      ? html`
												<mdc-icon-toggle-button
													checked=${button.options?.checked || false}
													type=${button.options?.type || 'tertiary'}
													size=${button.options?.size || 'md'}
													onIcon=${button.options?.onIcon || 'eye'}
													offIcon=${button.options?.offIcon || 'eye-slash'}
												></mdc-icon-toggle-button>
											`
                      : html`
												<mdc-icon-button
													type=${button.options?.type || 'tertiary'}
													size=${button.options?.size || 'lg'}
													iconSize=${button.options?.iconSize || 'default'}
													icon=${button.options?.icon || 'close'}
													label=${button.options?.label || ''}
												></mdc-icon-button>
											`
                  }
								</span>
							`
              : nothing
          }
				</div>
				${this.hint ? html`<span class="mdc-text-field__hint">${this.hint}</span>` : nothing}
			</div>
		`
  }
}
